"use client";

import { X } from "lucide-react";
import type { CatalogItem, CatalogType } from "@/lib/services/catalog/catalog-types";

interface EnlargedArtworkModalProps {
  artwork: CatalogItem | null;
  onClose: () => void;
  catalogType?: CatalogType;
}

export function EnlargedArtworkModal({
  artwork,
  onClose,
  catalogType = "all",
}: EnlargedArtworkModalProps) {
  if (!artwork) return null;

  // Katalog tipine göre etiket bilgilerini al
  const getCatalogTypeTag = (
    type: CatalogType
  ): { label: string; bgColor: string; textColor: string } => {
    switch (type) {
      case "drop":
        return {
          label: "DROP",
          bgColor: "bg-zinc-900",
          textColor: "text-zinc-100",
        };
      case "collection":
        return {
          label: "COLLECTION",
          bgColor: "bg-zinc-800",
          textColor: "text-zinc-100",
        };
      case "oneofone":
        return {
          label: "1/1",
          bgColor: "bg-black",
          textColor: "text-white",
        };
      case "installation":
        return {
          label: "INSTALLATION",
          bgColor: "bg-zinc-200",
          textColor: "text-zinc-800",
        };
      case "physical":
        return {
          label: "PHYSICAL",
          bgColor: "bg-zinc-100",
          textColor: "text-zinc-800",
        };
      case "immersive":
        return {
          label: "IMMERSIVE",
          bgColor: "bg-zinc-700",
          textColor: "text-zinc-100",
        };
      case "exhibition":
        return {
          label: "EXHIBITION",
          bgColor: "bg-zinc-300",
          textColor: "text-zinc-900",
        };
      case "all":
      default:
        return { label: "", bgColor: "", textColor: "" };
    }
  };

  const typeTag = getCatalogTypeTag(catalogType);
  const showCatalogTypeTag = catalogType !== "all";

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      <div
        className="relative max-w-5xl max-h-[90vh] bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
          onClick={onClose}
        >
          <X size={32} />
        </button>
        <div className="relative overflow-hidden">
          <img
            src={artwork.imageUrl || artwork.thumbnail}
            alt={artwork.title}
            className="max-h-[80vh] w-auto object-contain"
          />

          {/* Katalog tipi etiketi */}
          {showCatalogTypeTag && (
            <div
              className={`absolute top-4 right-4 ${typeTag.bgColor} ${typeTag.textColor} text-[14px] px-3 py-1 font-mono rounded-sm`}
            >
              {typeTag.label}
            </div>
          )}
        </div>
        <div className="p-4 bg-white">
          <h2 className="text-[14px] font-mono uppercase">{artwork.title}</h2>
          <p className="text-[12px] mt-1">{artwork.year}</p>
          <div className="flex flex-wrap gap-1 mt-2">
            {artwork.tags &&
              artwork.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] bg-zinc-100 px-2 py-0.5 font-mono"
                >
                  {tag}
                </span>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
